// AttendanceIntegratedBody.jsx — 출퇴근 통합 위젯 본문 (2026-06-18)
//
// 출퇴근 통계(attendance_today) 와 동일 UI/UX 를 회사별로 제공:
//   - 회사별 출근/퇴근/미퇴근 3컬럼(StatColumns) + 출근 중 이름(NamePills)
//   - 숫자 클릭 시 회사별 drill-down 모달(DetailModal) — 출퇴근 통계의 onClickCheckedIn 류와 동일
//
// 자체 모달 상태를 가지는 컴포넌트라 별도 파일로 분리 (renders 는 순수 render 함수만 유지).
// 데이터: { lines: [{ companyCode, payload: { checkedIn, checkedOut, notCheckedOut,
//          checkedInNames, rows: [{ name, checkIn, checkOut }], total } }] }

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { StatColumns, NamePills } from './bigCards/parts';
import { DetailModal } from './DetailModal';
import { useIsMobile } from './utils';

export function AttendanceIntegratedBody({ data }) {
  const [detail, setDetail] = useState(null);
  const isMobile = useIsMobile();

  const lines = Array.isArray(data && data.lines) ? data.lines : [];
  if (lines.length === 0) {
    return <span className="text-[10px] text-[var(--text-muted)]">없음</span>;
  }

  const openDetail = (title, rows) => setDetail({ title, rows, count: rows.length, countSuffix: '명' });

  return (
    <div className="w-full flex flex-col gap-2.5">
      {lines.map((line, i) => {
        const cc = String(line.companyCode || '').toUpperCase();
        const p = line.payload || {};
        const rows = Array.isArray(p.rows) ? p.rows : [];
        const checkedIn = Number(p.checkedIn || 0);
        const checkedOut = Number(p.checkedOut || 0);
        const notCheckedOut = Number(p.notCheckedOut || 0);
        const names = Array.isArray(p.checkedInNames) ? p.checkedInNames : [];
        const total = Number(p.total || 0);

        // 숫자 클릭 → 회사별 명단 모달 (출퇴근 통계와 동일 항목 구성)
        const onIn = () => openDetail(
          `[${cc}] 오늘 출근`,
          rows.filter((r) => r.checkIn).map((r) => ({ primary: r.name || '-', right: r.checkIn || '-', rightSub: '출근시간' })),
        );
        const onOut = () => openDetail(
          `[${cc}] 오늘 퇴근`,
          rows.filter((r) => r.checkOut).map((r) => ({ primary: r.name || '-', secondary: `출근 ${r.checkIn || '-'}`, right: r.checkOut, rightSub: '퇴근시간' })),
        );
        const onNot = () => openDetail(
          `[${cc}] 미퇴근`,
          rows.filter((r) => r.checkIn && !r.checkOut).map((r) => ({ primary: r.name || '-', secondary: `출근 ${r.checkIn || '-'}`, right: '미퇴근' })),
        );

        return (
          <div key={`a-${cc}-${i}`} className="w-full">
            <div className="flex items-baseline justify-between mb-1">
              <span className="text-[11px] font-black text-[var(--text-sub)]">[{cc}]</span>
              <span className="text-[10px] font-semibold text-[var(--text-muted)]">대상 {total}명</span>
            </div>
            {/* StatColumns onClick 은 내부에서 stopPropagation → 메뉴 navigate 차단 + 모달 오픈 */}
            <StatColumns
              emphasis="lg"
              items={[
                { label: '출근', value: checkedIn, color: '#059669', onClick: onIn },
                { label: '퇴근', value: checkedOut, color: 'var(--text-sub)', onClick: onOut },
                { label: '미퇴근', value: notCheckedOut, color: notCheckedOut > 0 ? '#dc2626' : 'var(--text-sub)', danger: notCheckedOut > 0, onClick: onNot },
              ]}
            />
            {names.length > 0 && (
              <div className="mt-1 pt-1 border-t border-[var(--border)]">
                <div className="text-[10px] font-bold text-[var(--text-sub)] mb-0.5 uppercase tracking-wider">출근 중</div>
                <NamePills names={names} />
              </div>
            )}
          </div>
        );
      })}

      {/* 모달은 document.body 로 portal — 카드의 overflow-hidden/transform 컨테이닝 블록을
          벗어나 fixed 가 뷰포트 기준으로 동작(위젯 안에 갇히지 않음).
          portal 자식도 React 트리로 이벤트가 버블되므로 stopPropagation 래퍼 유지 →
          모달 조작이 카드 navigate/드래그를 트리거하지 않음. */}
      {detail && createPortal(
        <div onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
          <DetailModal detail={detail} onClose={() => setDetail(null)} isMobile={isMobile} />
        </div>,
        document.body,
      )}
    </div>
  );
}
